import { getAuth } from 'firebase/auth';
import EnvironmentManager from '../config/EnvironmentManager';
import { getUserTimeZone } from '../utils/timezone';

const baseUrl = EnvironmentManager.baseUrl;

const getAuthHeaders = async (isMultipart = false) => {
  const auth = getAuth();
  const user = auth.currentUser;
  
  if (!user) {
    throw new Error("User must be logged in to apply.");
  }

  const token = await user.getIdToken();
  const headers = {
    'Authorization': `Bearer ${token}`
  };

  if (!isMultipart) {
    headers['Content-Type'] = 'application/json';
  }

  return headers;
};

export const jobApplicationService = {
  
  createDraft: async (jobId, userId) => {
    try {
      const headers = await getAuthHeaders();
     
      const response = await fetch(`${baseUrl}${EnvironmentManager.endpoints.createJobApplicationDraft}/${jobId}/${userId}`, {
        method: 'POST',
        headers: headers
      });

      if (!response.ok) {
        const errData = await response.text();
        throw new Error(errData || 'Failed to create application draft');
      }

      const applicationId = await response.text();
      return { success: true, applicationId: applicationId.trim() };
    } catch (error) {
      console.error('Error in createDraft:', error);
      return { success: false, error: error.message };
    }
  },

  saveBasicDetails: async (applicationId, data) => {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${baseUrl}${EnvironmentManager.endpoints.saveJobApplicationBasicDetails}/${applicationId}`, {
        method: 'PUT',
        headers: headers,
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errData = await response.text();
        throw new Error(errData || 'Failed to save basic details');
      }

      return { success: true };
    } catch (error) {
      console.error('Error in saveBasicDetails:', error);
      return { success: false, error: error.message };
    }
  },

  uploadResume: async (applicationId, file) => {
    try {
      const headers = await getAuthHeaders(true); 
      
      const formData = new FormData();
      formData.append('resume', file);

  
      const response = await fetch(`${baseUrl}${EnvironmentManager.endpoints.uploadJobApplicationResume}/${applicationId}`, {
        method: 'PUT',
        headers: headers,
        body: formData
      });

      if (!response.ok) {
        const errData = await response.text();
        throw new Error(errData || 'Failed to upload resume');
      }

      return { success: true };
    } catch (error) {
      console.error('Error in uploadResume:', error);
      return { success: false, error: error.message };
    }
  },

  saveScreeningAnswers: async (applicationId, answers) => {
    try {
      const headers = await getAuthHeaders();
  
      const response = await fetch(`${baseUrl}${EnvironmentManager.endpoints.saveJobApplicationScreening}/${applicationId}`, {
        method: 'PUT',
        headers: headers,
        body: JSON.stringify(answers)
      });

      if (!response.ok) {
        const errData = await response.text();
        throw new Error(errData || 'Failed to save screening answers');
      }

      return { success: true };
    } catch (error) {
      console.error('Error in saveScreeningAnswers:', error);
      return { success: false, error: error.message };
    }
  },

  getCompleteApplication: async (applicationId) => {
    try {
      const headers = await getAuthHeaders();
      const zoneId = getUserTimeZone();
   
      const response = await fetch(`${baseUrl}${EnvironmentManager.endpoints.getJobApplication}/${applicationId}?zoneId=${encodeURIComponent(zoneId)}`, {
        method: 'GET',
        headers: headers
      });

      if (!response.ok) {
        throw new Error('Failed to fetch complete application');
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Error in getCompleteApplication:', error);
      return { success: false, error: error.message };
    }
  },

  submitApplication: async (applicationId) => {
    try {
      const headers = await getAuthHeaders();
   
      const response = await fetch(`${baseUrl}${EnvironmentManager.endpoints.submitJobApplication}/${applicationId}`, {
        method: 'PUT',
        headers: headers
      });

      if (!response.ok) {
        const errData = await response.text();
        throw new Error(errData || 'Failed to submit application');
      }

      return { success: true };
    } catch (error) {
      console.error('Error in submitApplication:', error);
      return { success: false, error: error.message };
    }
  },

  // --- Status APIs ---

  getApplicationStatus: async (userId, jobId) => {
    try {
      const headers = await getAuthHeaders();
      const zoneId = getUserTimeZone();
     
      const response = await fetch(
        `${baseUrl}${EnvironmentManager.endpoints.getJobApplicationStatus}/${userId}/${jobId}?zoneId=${encodeURIComponent(zoneId)}`,
        { method: 'GET', headers }
      );
      if (!response.ok) {
        if (response.status === 404) return { success: true, data: null }; 
        throw new Error('Failed to fetch application status');
      }
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Error in getApplicationStatus:', error);
      return { success: false, error: error.message };
    }
  },

  getUserApplications: async (userId) => {
    try {
      const headers = await getAuthHeaders();
      const zoneId = getUserTimeZone();
      
      const response = await fetch(
        `${baseUrl}${EnvironmentManager.endpoints.getUserApplications}/${userId}?zoneId=${encodeURIComponent(zoneId)}`,
        { method: 'GET', headers }
      );
      if (!response.ok) {
        const errData = await response.text();
        throw new Error(errData || 'Failed to fetch user applications');
      }
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Error in getUserApplications:', error);
      return { success: false, error: error.message };
    }
  },

  updateEmployerStatus: async (applicationId, status) => {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(
        `${baseUrl}${EnvironmentManager.endpoints.updateEmployerApplicationStatus}/${applicationId}/employer-status`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify({ status }),
        }
      );
      if (!response.ok) {
        const errData = await response.text();
        throw new Error(errData || 'Failed to update application status');
      }
      return { success: true };
    } catch (error) {
      console.error('Error in updateEmployerStatus:', error);
      return { success: false, error: error.message };
    }
  },

  getJobApplicants: async (jobId) => {
    try {
      const headers = await getAuthHeaders();
      const zoneId = getUserTimeZone();

      const response = await fetch(
        `${baseUrl}${EnvironmentManager.endpoints.getJobApplicants}/${jobId}?zoneId=${encodeURIComponent(zoneId)}`,
        { method: 'GET', headers }
      );
      if (!response.ok) {
        const errData = await response.text();
        throw new Error(errData || 'Failed to fetch applicants');
      }
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Error in getJobApplicants:', error);
      return { success: false, error: error.message };
    }
  },

  // --- Draft Application APIs ---

  getDraftApplications: async (userId) => {
    try {
      const headers = await getAuthHeaders();
      const zoneId = getUserTimeZone();
     
      const response = await fetch(
        `${baseUrl}${EnvironmentManager.endpoints.getDraftApplications}/${userId}?zoneId=${encodeURIComponent(zoneId)}`,
        { method: 'GET', headers }
      );
      if (!response.ok) {
        if (response.status === 404) return { success: true, data: [] };
        const errData = await response.text();
        throw new Error(errData || 'Failed to fetch draft applications');
      }
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Error in getDraftApplications:', error);
      return { success: false, error: error.message };
    }
  },

  getDraftApplicationById: async (userId, jobId) => {
    try {
      const headers = await getAuthHeaders();
      const zoneId = getUserTimeZone();
    
      const response = await fetch(
        `${baseUrl}${EnvironmentManager.endpoints.getDraftApplicationById}/${userId}/${jobId}?zoneId=${encodeURIComponent(zoneId)}`,
        { method: 'GET', headers }
      );
      if (!response.ok) {
        const errData = await response.text();
        throw new Error(errData || 'Failed to fetch draft application');
      }
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Error in getDraftApplicationById:', error);
      return { success: false, error: error.message };
    }
  },

  discardDraftApplication: async (applicationId) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${baseUrl}/mv-profile/job-application/discard/${applicationId}`, {
        method: 'DELETE',
        headers: headers
      });

      if (!response.ok) {
        const errData = await response.text();
        throw new Error(errData || 'Failed to discard draft application');
      }

      return { success: true };
    } catch (error) {
      console.error('Error in discardDraftApplication:', error);
      return { success: false, error: error.message };
    }
  },

};
